import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { eventApiMessage } from '@/components/events/event-errors'
import { patchJSON } from '@/lib/api'
import { useViewOnly } from '@/components/templates/app-shell'

type Order = {
  id: number
  status: 'confirmed' | 'cancelled'
  checked_in_attendees?: string[]
  invoice: null | { status: string; balance: string; total?: string }
}
type OrderBook = {
  orders: Order[]
  buyer_groups: { buyer: { id: number; name: string; email: string }; orders: Order[] }[]
}
export function EventOrderCancellation({ eventId, order }: { eventId: number; order: Order }) {
  const viewOnly = useViewOnly()
  const [open, setOpen] = React.useState(false)
  const client = useQueryClient()
  const choose = ['Partially Paid', 'Paid'].includes(order.invoice?.status ?? '')
  const cancel = useMutation({
    mutationFn: (choice?: 'void-to-credit' | 'void-and-refund') =>
      patchJSON<{ order: Order }>(
        `/api/v1/events/${eventId}/orders/${order.id}/cancel`,
        choice ? { settlement_choice: choice } : {},
      ),
    onSuccess: ({ order: saved }) => {
      client.setQueriesData<OrderBook>({ queryKey: ['event-orders', eventId] }, (current) => {
        if (!current) return current
        const replace = (order: Order) => (order.id === saved.id ? saved : order)
        return {
          ...current,
          orders: current.orders.map(replace),
          buyer_groups: current.buyer_groups.map((group) => ({
            ...group,
            orders: group.orders.map(replace),
          })),
        }
      })
      setOpen(false)
    },
  })
  const detail = choose
    ? 'Choose whether received money becomes Account Credit or is refunded to the payer.'
    : order.invoice
      ? 'The unpaid invoice will be voided.'
      : 'This free order has no invoice.'
  const checkedInWarning = (order.checked_in_attendees ?? []).map(
    (name) => `${name} is checked in. Cancelling this order refunds their seat anyway.`,
  )
  if (viewOnly) return null
  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        {choose ? 'Cancel order and choose settlement' : 'Cancel order'}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel order?</DialogTitle>
            <DialogDescription>
              This keeps the order and its attendees as history. {detail}
            </DialogDescription>
          </DialogHeader>
          {checkedInWarning.length > 0 && (
            <div className="space-y-1 text-body-sm" aria-label="Checked-in attendees">
              {checkedInWarning.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </div>
          )}
          {cancel.error && <Alert>{eventApiMessage(cancel.error)}</Alert>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Keep order
            </Button>
            {choose ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={cancel.isPending}
                  onClick={() => cancel.mutate('void-to-credit')}
                >
                  Void to credit
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={cancel.isPending}
                  onClick={() => cancel.mutate('void-and-refund')}
                >
                  Void and refund
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="destructive"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(undefined)}
              >
                Cancel order
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
